import React, { useEffect, useMemo, useState } from 'react'
import { Container, Row, Col, Form } from 'reactstrap'

import useAsyncService from '../../hooks/useAsyncService'
import {
    GetDeviceUpdateAuditParams,
    DeviceUpdateAuditItem,
    SoftwareConformanceComponent,
    SoftwareConformanceComponentName,
    getDeviceUpdateAudit,
    remediateDeviceSoftwareUpdate,
    remediateDeviceSoftwareUpdates
} from '../../services/endpoints/devices'
import { formatError } from '../../redux/lib/errors'

import ComponentBox from '../../components/ComponentBox/ComponentBox'
import ICSFleetPicker from '../../components/ICSFleetPicker/ICSFleetPicker'
import { useForm } from 'react-hook-form'
import ICSButton from '../../components/ICS/Button/ICSButton'
import { useAppSelector } from '../../hooks/useAppSelector'
import { useTimezone } from '../../hooks/useTimezone'
import { DeviceUpdatesSearchSettings } from './DeviceUpdatesSearchSettings'
import { FLEET_MANAGER_AUTO_SELECT_ALL_PREF } from './DeviceUpdatesSettingsModal'
import CoreminiChangeWarningModal from './CoreminiChangeWarningModal'
import { LoadIndicator } from 'devextreme-react/load-indicator'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import { TanStackDatagrid } from '../../components'
import {
    TanStackDatagridColumn,
    TanStackDatagridFilter
} from '../../components/TanStackDataGrid/TanStackDatagrid'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import { applyColumnFilters } from './applyColumnFilters'
import {
    DEFAULT_FLEET_MANAGER_CSV_FILENAME,
    exportFleetManagerRowsToCsv,
    formatRemediationValue,
    formatTimestampedValue,
    STATUS_LABELS
} from './fleetManagerCsvExport'
import './DeviceUpdates.css'

export interface _DataSearchValues {
    fleetPicker: {
        organizations: number[]
        fleets: number[]
    }
    os: string
    client: string
    zchip: string
    schip: string
    script: string
    showOnlyMismatches: boolean
    showOnlyMatches: boolean
}

export interface _DeviceUpdatesSearchBoxProps {
    onSearch: (data: _DataSearchValues) => void
}

type MatchState = 'match' | 'pending' | 'mismatch' | 'unconfigured'

type RowStatus = 'match' | 'pending' | 'mismatch'

type MatchCell = {
    value: string
    state: MatchState
    tooltip: string
}

type DeviceUpdatesAuditGridRow = {
    id: number
    status: RowStatus
    statusTooltip: string
    vehicleName: string
    deviceName: string
    targetName: string
    targetSource: string | null
    osCell: MatchCell
    clientCell: MatchCell
    zchipCell: MatchCell
    schipCell: MatchCell
    scriptCell: MatchCell
    lastScriptName: string
    lastScriptTimestamp: string | null
    lastIupName: string
    lastIupTimestamp: string | null
    remediationText: string
    canRemediate: boolean
    deviceUpdateName: string
    coreminiWillChange: boolean
    coreminiActual: string | null
    coreminiExpected: string | null
}

type CoreminiWarningState =
    | { kind: 'single', row: DeviceUpdatesAuditGridRow }
    | { kind: 'bulk', rows: DeviceUpdatesAuditGridRow[] }

type RemediationHandlers = {
    onFix: (row: DeviceUpdatesAuditGridRow) => void
    fixingDeviceId: number | null
    isRemediating: boolean
}

const MATCH_TEXT_CLASS: Record<MatchState, string> = {
    match: 'text-success',
    pending: 'text-warning',
    mismatch: 'text-danger',
    unconfigured: ''
}

const MATCH_ICON: Record<RowStatus, string> = {
    match: '●',
    pending: '●',
    mismatch: '●'
}

const TARGET_SOURCE_LABELS: Record<string, string> = {
    VEHICLE: 'Vehicle',
    FLEET: 'Fleet',
    ORGANIZATION: 'Organization'
}

const defaultSearchValues: _DataSearchValues = {
    fleetPicker: {
        organizations: [],
        fleets: []
    },
    os: '',
    client: '',
    zchip: '',
    schip: '',
    script: '',
    showOnlyMismatches: false,
    showOnlyMatches: false
}

const hasManualVersionCriteria = (values: _DataSearchValues): boolean => (
    [values.os, values.client, values.zchip, values.schip, values.script]
        .some((value) => value.trim() !== '')
)

const displayVersionValue = (value: string | null | undefined): string => {
    const normalizedValue = value?.trim() ?? ''
    return normalizedValue === '' ? 'null' : normalizedValue
}

const buildMatchCell = (
    value: string,
    isMatch: boolean,
    hasPending: boolean,
    expectedValue: string
): MatchCell => {
    if (isMatch) {
        return {
            value,
            state: 'match',
            tooltip: 'Matches expected value'
        }
    }

    if (hasPending) {
        return {
            value,
            state: 'pending',
            tooltip: 'Update pending'
        }
    }

    return {
        value,
        state: 'mismatch',
        tooltip: `Mismatch (Expected ${expectedValue})`
    }
}

const buildManualVersionMatchCell = (
    actualValue: string | null | undefined,
    expectedValue: string,
    hasPending: boolean
): MatchCell => {
    const normalizedExpectedValue = expectedValue.trim()
    const normalizedActualValue = actualValue?.trim() ?? ''
    const hasActualValue = normalizedActualValue !== ''

    return buildMatchCell(
        hasActualValue ? normalizedActualValue : 'null',
        hasActualValue && (
            normalizedExpectedValue === ''
            || normalizedActualValue === normalizedExpectedValue
        ),
        hasPending,
        normalizedExpectedValue === '' ? 'a populated value' : normalizedExpectedValue
    )
}

const buildManualScriptMatchCell = (
    actualName: string | null | undefined,
    expectedName: string,
    hasPending: boolean
): MatchCell => {
    const normalizedExpectedName = expectedName.trim()
    const normalizedActualName = actualName?.trim() ?? ''
    const hasActualName = normalizedActualName !== ''
    const isMatch = hasActualName
        && normalizedActualName.toLowerCase().includes(normalizedExpectedName.toLowerCase())

    return buildMatchCell(
        hasActualName ? normalizedActualName : 'null',
        isMatch,
        hasPending,
        `a script name containing "${normalizedExpectedName}"`
    )
}

const buildVersionMatchCell = (
    actualValue: string | null | undefined,
    component: SoftwareConformanceComponent | undefined,
    hasPending: boolean
): MatchCell => {
    if (!component) {
        return {
            value: displayVersionValue(actualValue),
            state: 'unconfigured',
            tooltip: 'No expected version configured'
        }
    }

    return buildMatchCell(
        displayVersionValue(component.actual ?? actualValue),
        component.match,
        hasPending,
        component.expected
    )
}

const componentByName = (
    components: SoftwareConformanceComponent[],
    name: SoftwareConformanceComponentName
): SoftwareConformanceComponent | undefined => (
    components.find((component) => component.name === name)
)

const renderStatusCell = (data: DeviceUpdatesAuditGridRow) => (
    <span
        className={MATCH_TEXT_CLASS[data.status]}
        title={data.statusTooltip}
        style={{ fontSize: '1rem', lineHeight: 1 }}
    >
        {MATCH_ICON[data.status]}
    </span>
)

const renderMatchCell = (cell: MatchCell) => (
    <span className={MATCH_TEXT_CLASS[cell.state]} title={cell.tooltip}>
        {cell.value}
    </span>
)

const renderTimestampedValue = (value: string, timestamp: string | null) => {
    const tooltip = timestamp ? `${value}\n${timestamp}` : value

    return (
        <div className="py-1" title={tooltip}>
            <div>{value}</div>
            {timestamp && <div className="small text-muted">{timestamp}</div>}
        </div>
    )
}

const renderTargetCell = (data: DeviceUpdatesAuditGridRow) => {
    if (!data.targetSource) {
        return <span title="No device software version configured">{data.targetName}</span>
    }

    return (
        <div className="py-1" title={`${data.targetName} (${data.targetSource})`}>
            <div>{data.targetName}</div>
            <div className="small text-muted">{data.targetSource}</div>
        </div>
    )
}

const renderRemediationCell = (
    data: DeviceUpdatesAuditGridRow,
    handlers: RemediationHandlers
) => {
    if (data.status === 'pending' || !data.canRemediate) {
        return null
    }

    const isFixing = handlers.fixingDeviceId === data.id

    return (
        <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={handlers.isRemediating}
            aria-label="Fix"
            title={`Send ${data.deviceUpdateName} to ${data.deviceName}`}
            onClick={() => handlers.onFix(data)}
        >
            {isFixing ? 'Sending…' : 'Fix'}
        </button>
    )
}

const buildFleetManagerColumns = (
    handlers: RemediationHandlers
): TanStackDatagridColumn<DeviceUpdatesAuditGridRow>[] => [
    {
        id: 'status',
        header: 'Status',
        accessor: (row) => STATUS_LABELS[row.status],
        cell: renderStatusCell,
        hideable: false,
        width: 70,
        className: 'text-center',
        headerClassName: 'text-center'
    },
    {
        id: 'vehicleName',
        header: 'Vehicle',
        accessor: (row) => row.vehicleName,
        filterable: true,
        hideable: false,
        minWidth: 160
    },
    {
        id: 'deviceName',
        header: 'Device',
        accessor: (row) => row.deviceName,
        filterable: true,
        hideable: false,
        minWidth: 180
    },
    {
        id: 'target',
        header: 'Target',
        accessor: (row) => (
            row.targetSource ? `${row.targetName} (${row.targetSource})` : row.targetName
        ),
        cell: renderTargetCell,
        filterable: true,
        hideable: false,
        width: 120,
        minWidth: 100,
        maxWidth: 150
    },
    {
        id: 'os',
        header: 'OS',
        accessor: (row) => row.osCell.value,
        cell: (row) => renderMatchCell(row.osCell),
        filterable: true,
        hideable: false,
        width: 96,
        minWidth: 80,
        maxWidth: 120
    },
    {
        id: 'client',
        header: 'Client',
        accessor: (row) => row.clientCell.value,
        cell: (row) => renderMatchCell(row.clientCell),
        filterable: true,
        hideable: false,
        width: 104,
        minWidth: 88,
        maxWidth: 130
    },
    {
        id: 'zchip',
        header: 'ZChip',
        accessor: (row) => row.zchipCell.value,
        cell: (row) => renderMatchCell(row.zchipCell),
        filterable: true,
        hideable: false,
        width: 96,
        minWidth: 80,
        maxWidth: 120
    },
    {
        id: 'schip',
        header: 'SChip',
        accessor: (row) => row.schipCell.value,
        cell: (row) => renderMatchCell(row.schipCell),
        filterable: true,
        hideable: false,
        width: 96,
        minWidth: 80,
        maxWidth: 120
    },
    {
        id: 'script',
        header: 'Script',
        accessor: (row) => row.scriptCell.value,
        cell: (row) => renderMatchCell(row.scriptCell),
        filterable: true,
        hideable: false,
        minWidth: 170
    },
    {
        id: 'lastScript',
        header: 'Last Script',
        accessor: (row) => formatTimestampedValue(row.lastScriptName, row.lastScriptTimestamp),
        cell: (row) => renderTimestampedValue(row.lastScriptName, row.lastScriptTimestamp),
        filterable: true,
        hideable: false,
        minWidth: 180
    },
    {
        id: 'lastIup',
        header: 'Last IUP',
        accessor: (row) => formatTimestampedValue(row.lastIupName, row.lastIupTimestamp),
        cell: (row) => renderTimestampedValue(row.lastIupName, row.lastIupTimestamp),
        filterable: true,
        hideable: false,
        minWidth: 180
    },
    {
        id: 'remediation',
        header: 'Remediation',
        accessor: (row) => formatRemediationValue(row),
        cell: (row) => renderRemediationCell(row, handlers),
        hideable: false,
        width: 110,
        minWidth: 96,
        maxWidth: 130,
        className: 'text-center',
        headerClassName: 'text-center'
    }
]

const DeviceUpdatesSearchBox: React.FC<_DeviceUpdatesSearchBoxProps> = ({ onSearch }) => {
    const {
        handleSubmit,
        control,
        register,
        setValue,
        getValues,
        watch,
        formState: { isSubmitting, isValid }
    } = useForm<_DataSearchValues>({
        mode: 'onChange',
        defaultValues: defaultSearchValues
    })
    const [showVersionCriteria, setShowVersionCriteria] = useState(false)
    const watchedVersionCriteria = watch(['os', 'client', 'zchip', 'schip', 'script'])
    const hasActiveManualCriteria = watchedVersionCriteria.some((value) => (value ?? '').trim() !== '')
    const autoSelectAll = useAppSelector((state) => (
        (state.userPreference.data[FLEET_MANAGER_AUTO_SELECT_ALL_PREF]?.val || 'false') === 'true'
    ))

    const {
        onChange: onShowOnlyMismatchesChange,
        ...showOnlyMismatchesRegister
    } = register('showOnlyMismatches')
    const {
        onChange: onShowOnlyMatchesChange,
        ...showOnlyMatchesRegister
    } = register('showOnlyMatches')

    const searchData = (data: _DataSearchValues) => {
        onSearch(data)
    }

    return (
        <Form onSubmit={handleSubmit(searchData)}>
            <ComponentBox
                isCustom={true}
                cardClass="groups-results-container"
                cardBodyId="groupsCardBody"
                title="Search Criteria"
                accordion={true}
            >
                <Row>
                    <Col xs={12} md={6}>
                        <ICSFleetPicker<_DataSearchValues, 'fleetPicker'>
                            name="fleetPicker"
                            control={control}
                            autoSelectAll={autoSelectAll}
                            onAutoSelected={(fleetPicker) => {
                                onSearch({
                                    ...getValues(),
                                    fleetPicker
                                })
                            }}
                        />
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="border rounded bg-white p-3 h-100">
                            <h4 className="mb-3">Filters</h4>
                            <div className="form-check">
                                <input
                                    id="device-updates-show-only-mismatches"
                                    className="form-check-input"
                                    type="checkbox"
                                    {...showOnlyMismatchesRegister}
                                    onChange={(event) => {
                                        onShowOnlyMismatchesChange(event)
                                        if (event.target.checked) {
                                            setValue('showOnlyMatches', false)
                                        }
                                    }}
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="device-updates-show-only-mismatches"
                                >
                                    Show only mismatched devices
                                </label>
                            </div>
                            <div className="form-check mt-2">
                                <input
                                    id="device-updates-show-only-matches"
                                    className="form-check-input"
                                    type="checkbox"
                                    {...showOnlyMatchesRegister}
                                    onChange={(event) => {
                                        onShowOnlyMatchesChange(event)
                                        if (event.target.checked) {
                                            setValue('showOnlyMismatches', false)
                                        }
                                    }}
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="device-updates-show-only-matches"
                                >
                                    Show only matched devices
                                </label>
                            </div>
                            <p className="small text-muted mt-3 mb-0">
                                Software versions are compared to the Device Software Version
                                configured on the vehicle, fleet, or organization.
                            </p>
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 mt-2"
                                onClick={() => setShowVersionCriteria((isShown) => !isShown)}
                            >
                                {showVersionCriteria ? 'Hide version criteria' : 'Show version criteria'}
                            </button>
                            {!showVersionCriteria && hasActiveManualCriteria && (
                                <p className="small text-muted mt-2 mb-0">
                                    Manual version criteria are active and will override configured
                                    conformance.
                                </p>
                            )}
                            {showVersionCriteria && (
                                <div className="mt-3">
                                    <Row>
                                        <Col xs={12} sm={6}>
                                            <label className="form-label" htmlFor="device-updates-os">
                                                OS
                                            </label>
                                            <input
                                                id="device-updates-os"
                                                className="form-control"
                                                placeholder="Exact OS version"
                                                {...register('os')}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <label className="form-label" htmlFor="device-updates-client">
                                                Client
                                            </label>
                                            <input
                                                id="device-updates-client"
                                                className="form-control"
                                                placeholder="Exact Client version"
                                                {...register('client')}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col xs={12} sm={6}>
                                            <label className="form-label" htmlFor="device-updates-zchip">
                                                ZChip
                                            </label>
                                            <input
                                                id="device-updates-zchip"
                                                className="form-control"
                                                placeholder="Exact ZChip version"
                                                {...register('zchip')}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <label className="form-label" htmlFor="device-updates-schip">
                                                SChip
                                            </label>
                                            <input
                                                id="device-updates-schip"
                                                className="form-control"
                                                placeholder="Exact SChip version"
                                                {...register('schip')}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col xs={12}>
                                            <label className="form-label" htmlFor="device-updates-script">
                                                Script name
                                            </label>
                                            <input
                                                id="device-updates-script"
                                                className="form-control"
                                                placeholder="Partial script name"
                                                {...register('script')}
                                            />
                                        </Col>
                                    </Row>
                                    <div className="alert alert-warning mt-3 mb-0" role="alert">
                                        Searching with any version or script criteria overrides the
                                        configured Device Software Version on the vehicle, fleet, or
                                        organization. Version fields use the exact values entered here.
                                        Script name is a partial match. Fix and Fix all are unavailable
                                        while any criterion is set.
                                    </div>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
                <Row className="mt-3 align-items-center">
                    <Col>
                        <DeviceUpdatesSearchSettings />
                    </Col>
                    <Col className="col-sm-auto">
                        <ICSButton
                            icon="search"
                            text="Search"
                            type="success"
                            onClick={handleSubmit(searchData)}
                            disabled={isSubmitting || !isValid}
                        />
                    </Col>
                </Row>
            </ComponentBox>
        </Form>
    )
}

export const DeviceUpdatesAuditResults = ({
    data: searchValues,
    searchNonce
}: {
    data: _DataSearchValues
    searchNonce: number
}) => {
    const { formatLocalTimeFromUtc } = useTimezone()
    const [filters, setFilters] = useState<TanStackDatagridFilter[]>([])
    const [refreshNonce, setRefreshNonce] = useState(0)
    const [fixingDeviceId, setFixingDeviceId] = useState<number | null>(null)
    const [isRemediating, setIsRemediating] = useState(false)
    const [fixError, setFixError] = useState<string | null>(null)
    const [coreminiWarning, setCoreminiWarning] = useState<CoreminiWarningState | null>(null)
    const requestArgs = useMemo(() => ({
        organizations: searchValues.fleetPicker.organizations,
        fleets: searchValues.fleetPicker.fleets,
        refreshNonce,
        searchNonce
    }), [searchValues.fleetPicker.fleets, searchValues.fleetPicker.organizations, refreshNonce, searchNonce])

    useEffect(() => {
        setFilters([])
        setFixError(null)
        setCoreminiWarning(null)
    }, [searchValues])

    const {
        serviceLoading,
        serviceError,
        serviceValue: devices,
        setServiceArgs
    } = useAsyncService<GetDeviceUpdateAuditParams, DeviceUpdateAuditItem[]>({
        serviceCall: getDeviceUpdateAudit,
        initialArgs: requestArgs
    })

    useEffect(() => {
        setServiceArgs(requestArgs)
    }, [requestArgs, setServiceArgs])

    const rows = useMemo(() => {
        if (!devices) return []

        const usesManualCriteria = hasManualVersionCriteria(searchValues)

        const mappedRows = devices.map((device): DeviceUpdatesAuditGridRow => {
            const pendingScriptJob = device.pendingFileSendJobs.find((job) => job.type === 'SCRIPT')
            const pendingSoftwareJob = device.pendingFileSendJobs.find((job) => job.type === 'SOFTWARE_UPDATE')
            const components = device.softwareConformance.components
            const hasPendingSoftwareJob = Boolean(pendingSoftwareJob)

            const osCell = usesManualCriteria
                ? buildManualVersionMatchCell(device.versions.os?.ver, searchValues.os, hasPendingSoftwareJob)
                : buildVersionMatchCell(
                    device.versions.os?.ver,
                    componentByName(components, 'os'),
                    hasPendingSoftwareJob
                )
            const clientCell = usesManualCriteria
                ? buildManualVersionMatchCell(device.versions.client?.ver, searchValues.client, hasPendingSoftwareJob)
                : buildVersionMatchCell(
                    device.versions.client?.ver,
                    componentByName(components, 'client'),
                    hasPendingSoftwareJob
                )
            const zchipCell = usesManualCriteria
                ? buildManualVersionMatchCell(device.versions.zchip?.ver, searchValues.zchip, hasPendingSoftwareJob)
                : buildVersionMatchCell(
                    device.versions.zchip?.ver,
                    componentByName(components, 'zchip'),
                    hasPendingSoftwareJob
                )
            const schipCell = usesManualCriteria
                ? buildManualVersionMatchCell(device.versions.schip?.ver, searchValues.schip, hasPendingSoftwareJob)
                : buildVersionMatchCell(
                    device.versions.schip?.ver,
                    componentByName(components, 'schip'),
                    hasPendingSoftwareJob
                )

            const expectedScriptName = device.script.lastSent?.downloadFile.name ?? 'null'
            const scriptMatches = device.script.lastSent
                ? device.script.lastSent.matchesCurrent
                : device.script.current === null
            const scriptCell = usesManualCriteria && searchValues.script.trim() !== ''
                ? buildManualScriptMatchCell(
                    device.script.current?.name,
                    searchValues.script,
                    Boolean(pendingScriptJob)
                )
                : buildMatchCell(
                    device.script.current?.name ?? 'null',
                    scriptMatches,
                    Boolean(pendingScriptJob),
                    expectedScriptName
                )

            const softwareMismatch = !usesManualCriteria
                && device.softwareConformance.status === 'mismatch'
                && !pendingSoftwareJob
            const cells = [osCell, clientCell, zchipCell, schipCell, scriptCell]
            const hasFailures = softwareMismatch || cells.some((cell) => cell.state === 'mismatch')
            const hasPending = cells.some((cell) => cell.state === 'pending')
            const status: RowStatus = hasFailures
                ? 'mismatch'
                : hasPending
                    ? 'pending'
                    : 'match'

            const remediationNames = new Set<string>()
            if (scriptCell.state === 'pending' && pendingScriptJob) {
                remediationNames.add(pendingScriptJob.downloadFile.name)
            }
            if (
                [osCell, clientCell, zchipCell, schipCell].some((cell) => cell.state === 'pending')
                && pendingSoftwareJob
            ) {
                remediationNames.add(pendingSoftwareJob.downloadFile.name)
            }

            const configuredUpdate = device.configuredSoftwareUpdate
            const targetName = configuredUpdate?.deviceUpdate.name ?? 'None'
            const targetSource = configuredUpdate
                ? (TARGET_SOURCE_LABELS[configuredUpdate.source] ?? configuredUpdate.source)
                : null

            return {
                id: device.device.id,
                status,
                statusTooltip: status === 'match'
                    ? 'Matches expected values'
                    : status === 'pending'
                        ? 'Update in progress'
                        : 'Mismatch detected',
                vehicleName: device.vehicle.name,
                deviceName: device.device.name,
                targetName,
                targetSource,
                osCell,
                clientCell,
                zchipCell,
                schipCell,
                scriptCell,
                lastScriptName: device.script.lastSent?.downloadFile.name ?? 'null',
                lastScriptTimestamp: device.script.lastSent?.download.dateCreated
                    ? formatLocalTimeFromUtc(device.script.lastSent.download.dateCreated)
                    : null,
                lastIupName: device.softwareUpdate.lastSent?.downloadFile.name ?? 'null',
                lastIupTimestamp: device.softwareUpdate.lastSent?.download.dateCreated
                    ? formatLocalTimeFromUtc(device.softwareUpdate.lastSent.download.dateCreated)
                    : null,
                remediationText: Array.from(remediationNames).join(', '),
                canRemediate: softwareMismatch,
                deviceUpdateName: configuredUpdate?.deviceUpdate.name ?? 'the configured software update',
                coreminiWillChange: device.coreminiChange?.willChange === true,
                coreminiActual: device.coreminiChange?.actual ?? null,
                coreminiExpected: device.coreminiChange?.expected ?? null
            }
        })

        if (searchValues.showOnlyMismatches) {
            return mappedRows.filter((row) => row.status !== 'match')
        }

        if (searchValues.showOnlyMatches) {
            return mappedRows.filter((row) => row.status === 'match')
        }

        return mappedRows
    }, [
        searchValues.client,
        searchValues.os,
        searchValues.schip,
        searchValues.script,
        searchValues.showOnlyMismatches,
        searchValues.showOnlyMatches,
        searchValues.zchip,
        devices,
        formatLocalTimeFromUtc
    ])

    const sendSingleFix = (row: DeviceUpdatesAuditGridRow) => {
        setCoreminiWarning(null)
        setFixError(null)
        setFixingDeviceId(row.id)
        setIsRemediating(true)
        remediateDeviceSoftwareUpdate(row.id)
            .then(() => {
                setRefreshNonce((currentNonce) => currentNonce + 1)
            })
            .catch((error: unknown) => {
                setFixError(formatError(error).message || 'Unable to send the software update')
            })
            .finally(() => {
                setFixingDeviceId(null)
                setIsRemediating(false)
            })
    }

    const sendBulkFix = (rowsToRemediate: DeviceUpdatesAuditGridRow[]) => {
        const deviceIds = rowsToRemediate.map((row) => row.id)
        if (deviceIds.length === 0) {
            setCoreminiWarning(null)
            return
        }

        setCoreminiWarning(null)
        setFixError(null)
        setIsRemediating(true)
        remediateDeviceSoftwareUpdates(deviceIds)
            .then((result) => {
                if (result.failed.length > 0) {
                    setFixError(
                        `Unable to send the software update to ${result.failed.length} of ${deviceIds.length} devices.`
                    )
                }
                setRefreshNonce((currentNonce) => currentNonce + 1)
            })
            .catch((error: unknown) => {
                setFixError(formatError(error).message || 'Unable to send the software update')
            })
            .finally(() => {
                setIsRemediating(false)
            })
    }

    const columns = useMemo(
        () => buildFleetManagerColumns({
            fixingDeviceId,
            isRemediating,
            onFix: (row) => {
                if (row.coreminiWillChange) {
                    setCoreminiWarning({ kind: 'single', row })
                    return
                }

                const confirmed = window.confirm(
                    `Send ${row.deviceUpdateName} to ${row.deviceName}?`
                )
                if (!confirmed) {
                    return
                }

                sendSingleFix(row)
            }
        }),
        [fixingDeviceId, isRemediating]
    )

    const filteredRows = useMemo(
        () => applyColumnFilters(rows, filters, columns),
        [filters, rows, columns]
    )

    const remediableRows = useMemo(
        () => filteredRows.filter((row) => row.canRemediate),
        [filteredRows]
    )

    const handleFixAll = () => {
        const deviceCount = remediableRows.length
        if (deviceCount === 0 || isRemediating) {
            return
        }

        if (remediableRows.some((row) => row.coreminiWillChange)) {
            setCoreminiWarning({ kind: 'bulk', rows: remediableRows })
            return
        }

        const confirmed = window.confirm(
            `Send the configured Device Software Version to ${deviceCount} ${
                deviceCount === 1 ? 'device' : 'devices'
            }?`
        )
        if (!confirmed) {
            return
        }

        sendBulkFix(remediableRows)
    }

    if (serviceLoading) {
        return <LoadIndicator />
    } else if (serviceError) {
        return (
            <ICSErrorCard
                title="An error occured"
                message={serviceError}
                code="error"
                fields={[]}
            />
        )
    }

    const coreminiChangeRows = coreminiWarning?.kind === 'bulk'
        ? coreminiWarning.rows.filter((row) => row.coreminiWillChange)
        : []
    const coreminiSkipRows = coreminiWarning?.kind === 'bulk'
        ? coreminiWarning.rows.filter((row) => !row.coreminiWillChange)
        : []

    return (
        <div id="fleet-manager-results">
            {coreminiWarning?.kind === 'single' && (
                <CoreminiChangeWarningModal
                    mode="single"
                    deviceName={coreminiWarning.row.deviceName}
                    deviceUpdateName={coreminiWarning.row.deviceUpdateName}
                    actual={coreminiWarning.row.coreminiActual}
                    expected={coreminiWarning.row.coreminiExpected}
                    disabled={isRemediating}
                    onCancel={() => setCoreminiWarning(null)}
                    onContinue={() => sendSingleFix(coreminiWarning.row)}
                />
            )}
            {coreminiWarning?.kind === 'bulk' && (
                <CoreminiChangeWarningModal
                    mode="bulk"
                    totalCount={coreminiWarning.rows.length}
                    changeCount={coreminiChangeRows.length}
                    skipCount={coreminiSkipRows.length}
                    disabled={isRemediating}
                    onCancel={() => setCoreminiWarning(null)}
                    onContinue={() => sendBulkFix(coreminiWarning.rows)}
                    onSkipCoreminiChanges={() => sendBulkFix(coreminiSkipRows)}
                />
            )}
            {fixError && (
                <div className="mb-3">
                    <ICSErrorCard
                        title="Unable to send software update"
                        message={fixError}
                        code="error"
                        fields={[]}
                    />
                </div>
            )}
            <ComponentBox
                isCustom={true}
                title="Results"
                accordion={false}
                cardBodyId="fleet-manager-results-body"
            >
                <div className="bg-white">
                    <TanStackDatagrid
                        testId="fleet-manager-grid"
                        data={rows}
                        columns={columns}
                        getRowId={(row) => String(row.id)}
                        emptyMessage="No devices matched the current filters."
                        enablePagination={false}
                        enableColumnSelection={false}
                        filters={filters}
                        onFilterChange={setFilters}
                    />
                    <div className="fleet-manager-results-footer d-flex justify-content-between align-items-center flex-wrap mt-2 px-1">
                        <div className="d-flex align-items-center flex-wrap" style={{ columnGap: '8px', rowGap: '8px' }}>
                            <ICSButton
                                id="fleet-manager-export-csv"
                                text="Export to CSV"
                                type="default"
                                stylingMode="outlined"
                                height={28}
                                disabled={filteredRows.length === 0}
                                onClick={() => exportFleetManagerRowsToCsv(
                                    filteredRows,
                                    DEFAULT_FLEET_MANAGER_CSV_FILENAME
                                )}
                            />
                            <ICSButton
                                id="fleet-manager-fix-all"
                                text={`Fix all (${remediableRows.length})`}
                                type="default"
                                stylingMode="contained"
                                height={28}
                                disabled={remediableRows.length === 0 || isRemediating}
                                onClick={handleFixAll}
                            />
                        </div>
                        <div
                            className="small d-flex align-items-center flex-wrap"
                            style={{ columnGap: '24px', rowGap: '8px' }}
                        >
                            <span className="d-inline-flex align-items-center text-muted">
                                <span className="text-success me-2" aria-hidden="true">●</span>
                                Match
                            </span>
                            <span className="d-inline-flex align-items-center text-muted">
                                <span className="text-warning me-2" aria-hidden="true">●</span>
                                Update in progress
                            </span>
                            <span className="d-inline-flex align-items-center text-muted">
                                <span className="text-danger me-2" aria-hidden="true">●</span>
                                Mismatch
                            </span>
                        </div>
                    </div>
                </div>
            </ComponentBox>
        </div>
    )
}

export const DeviceUpdatesPage: React.FC = () => {
    const [searchValues, setSearchValues] = useState<_DataSearchValues | null>(null)
    const [searchNonce, setSearchNonce] = useState(0)

    const handleSearch = (data: _DataSearchValues) => {
        setSearchValues(data)
        setSearchNonce((currentNonce) => currentNonce + 1)
    }

    return (
        <Container fluid={true} className="wivi-container">
            <Row>
                <Col xs={12}>
                    <HeaderBar
                        origin="Devices"
                        routes={[
                            {
                                title: 'Fleet Manager'
                            }
                        ]}
                        help={{
                            title: 'Fleet Manager',
                            text: 'Review firmware and script matches for devices in the selected organizations and fleets. Software versions are compared to the Device Software Version configured on each vehicle, fleet, or organization. Use Fix to send the configured IUP to a mismatched device.'
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <DeviceUpdatesSearchBox onSearch={handleSearch} />
                </Col>
            </Row>
            {searchValues && (
                <Row className="mt-3">
                    <Col xs={12}>
                        <DeviceUpdatesAuditResults data={searchValues} searchNonce={searchNonce} />
                    </Col>
                </Row>
            )}
        </Container>
    )
}

export default DeviceUpdatesPage
